import {
  useAuth,
  useChangePassword,
  useProfile,
  useUpdateEmail,
  useUpdateUsername,
} from "@/shared/hooks";
import { AvatarUpload } from "@/shared/ui";
import { GameLayout } from "@/widgets/layout";
import React, { useState } from "react";
import styles from "./ProfilePage.module.css";

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { data: profileData, isLoading: profileLoading } = useProfile();

  const updateUsernameMutation = useUpdateUsername();
  const updateEmailMutation = useUpdateEmail();
  const changePasswordMutation = useChangePassword();

  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false);

  const [usernameValue, setUsernameValue] = useState("");
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirmPassword: "",
  });
  const [emailValue, setEmailValue] = useState("");

  if (profileLoading) {
    return (
      <GameLayout>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingSpinner}></div>
            <h2 className={styles.loadingText}>Загрузка профиля...</h2>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!user) {
    return (
      <GameLayout>
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <h1 className={styles.errorTitle}>Пользователь не найден</h1>
            <p className={styles.errorText}>
              Не удалось загрузить данные профиля
            </p>
          </div>
        </div>
      </GameLayout>
    );
  }

  const displayName =
    profileData?.user.username || user.email?.split("@")[0] || "Пользователь";
  const userEmail = profileData?.user.email || user.email;
  const userAvatar = profileData?.user.avatar;

  const handleUsernameEdit = () => {
    setUsernameValue(displayName);
    setIsEditingUsername(true);
  };

  const handleUsernameSave = async () => {
    if (!usernameValue.trim()) return;

    try {
      await updateUsernameMutation.mutateAsync({
        username: usernameValue.trim(),
      });
      setIsEditingUsername(false);
    } catch (error) {
      console.error("Ошибка обновления имени:", error);
    }
  };

  const handleUsernameCancel = () => {
    setUsernameValue("");
    setIsEditingUsername(false);
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirmPassword) {
      alert("Пароли не совпадают");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync(passwordData);
      setPasswordData({
        old_password: "",
        new_password: "",
        confirmPassword: "",
      });
      setIsChangePasswordOpen(false);
      alert("Пароль успешно изменен");
    } catch (error) {
      console.error("Ошибка смены пароля:", error);
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  };

  const handleEmailChange = async () => {
    if (!emailValue.trim()) return;

    try {
      await updateEmailMutation.mutateAsync({ email: emailValue.trim() });
      setEmailValue("");
      setIsChangeEmailOpen(false);
      alert("Email успешно обновлен. Проверьте почту для подтверждения.");
    } catch (error) {
      console.error("Ошибка смены email:", error);
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  };

  return (
    <GameLayout>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>Профиль пользователя</h1>
            <p className={styles.subtitle}>
              Управление личной информацией и настройками
            </p>
          </div>

          <div className={styles.profileCard}>
            <div className={styles.profileContent}>
              <div className={styles.avatarContainer}>
                <AvatarUpload
                  currentAvatar={userAvatar || undefined}
                  username={displayName}
                  size="md"
                  className="mx-auto md:mx-0"
                />
              </div>

              <div className={styles.userInfo}>
                <h2 className={styles.userName}>{displayName}</h2>
                <p className={styles.userEmail}>{userEmail}</p>
              </div>
            </div>
          </div>

          <div className={styles.sections}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Личная информация</h3>
              <div className={styles.sectionContent}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Имя пользователя</label>
                  <div className={styles.fieldRow}>
                    {isEditingUsername ? (
                      <>
                        <input
                          type="text"
                          value={usernameValue}
                          onChange={(e) => setUsernameValue(e.target.value)}
                          className={styles.input}
                          placeholder="Введите имя пользователя"
                          autoFocus
                        />
                        <button
                          onClick={handleUsernameSave}
                          disabled={
                            updateUsernameMutation.isPending ||
                            !usernameValue.trim()
                          }
                          className={`${styles.button} ${styles.buttonGreen}`}
                        >
                          {updateUsernameMutation.isPending ? "..." : "✓"}
                        </button>
                        <button
                          onClick={handleUsernameCancel}
                          disabled={updateUsernameMutation.isPending}
                          className={`${styles.button} ${styles.buttonGray}`}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <p className={styles.fieldValue}>{displayName}</p>
                        <button
                          onClick={handleUsernameEdit}
                          className={styles.editButton}
                          title="Редактировать имя"
                        >
                          <svg
                            className={styles.editIcon}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Email адрес</label>
                  <p className={styles.fieldValue}>{userEmail}</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Дата регистрации</label>
                  <p className={styles.fieldValueSecondary}>
                    {profileData?.user.createdAt
                      ? new Date(profileData.user.createdAt).toLocaleDateString(
                          "ru-RU",
                        )
                      : "Информация будет добавлена позже"}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Настройки аккаунта</h3>
              <div className={styles.settingsSection}>
                <div className={styles.settingItem}>
                  <div className={styles.settingHeader}>
                    <label className={styles.fieldLabel}>Безопасность</label>
                    <button
                      onClick={() =>
                        setIsChangePasswordOpen(!isChangePasswordOpen)
                      }
                      className={styles.settingButton}
                    >
                      Изменить пароль
                    </button>
                  </div>

                  {isChangePasswordOpen && (
                    <div className={styles.settingForm}>
                      <input
                        type="password"
                        placeholder="Текущий пароль"
                        value={passwordData.old_password}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            old_password: e.target.value,
                          }))
                        }
                        className={styles.settingFormInput}
                      />
                      <input
                        type="password"
                        placeholder="Новый пароль"
                        value={passwordData.new_password}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            new_password: e.target.value,
                          }))
                        }
                        className={styles.settingFormInput}
                      />
                      <input
                        type="password"
                        placeholder="Повторить новый пароль"
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className={styles.settingFormInput}
                      />
                      <div className={styles.settingFormButtons}>
                        <button
                          onClick={handlePasswordChange}
                          disabled={
                            changePasswordMutation.isPending ||
                            !passwordData.old_password ||
                            !passwordData.new_password ||
                            !passwordData.confirmPassword
                          }
                          className={`${styles.settingFormButton} ${styles.settingFormButtonGreen}`}
                        >
                          {changePasswordMutation.isPending
                            ? "Сохранение..."
                            : "Сохранить"}
                        </button>
                        <button
                          onClick={() => {
                            setIsChangePasswordOpen(false);
                            setPasswordData({
                              old_password: "",
                              new_password: "",
                              confirmPassword: "",
                            });
                          }}
                          disabled={changePasswordMutation.isPending}
                          className={`${styles.settingFormButton} ${styles.settingFormButtonGray}`}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingHeader}>
                    <label className={styles.fieldLabel}>Email адрес</label>
                    <button
                      onClick={() => setIsChangeEmailOpen(!isChangeEmailOpen)}
                      className={styles.settingButton}
                    >
                      Изменить email
                    </button>
                  </div>

                  {isChangeEmailOpen && (
                    <div className={styles.settingForm}>
                      <input
                        type="email"
                        placeholder="Новый email адрес"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        className={styles.settingFormInput}
                      />
                      <div className={styles.settingFormButtons}>
                        <button
                          onClick={handleEmailChange}
                          disabled={
                            updateEmailMutation.isPending || !emailValue.trim()
                          }
                          className={`${styles.settingFormButton} ${styles.settingFormButtonGreen}`}
                        >
                          {updateEmailMutation.isPending
                            ? "Сохранение..."
                            : "Сохранить"}
                        </button>
                        <button
                          onClick={() => {
                            setIsChangeEmailOpen(false);
                            setEmailValue("");
                          }}
                          disabled={updateEmailMutation.isPending}
                          className={`${styles.settingFormButton} ${styles.settingFormButtonGray}`}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GameLayout>
  );
};
